import { createFileRoute, useParams, notFound, redirect, useNavigate } from '@tanstack/react-router'
import { SlidePreview } from '../../../../components/chat/SlidePreview'
import { conversationAPI, slidesAPI, templateAPI } from '../../../../services/api'
import slidePathMapping from '../../../../services/slideMapping'

interface SlideData {
  id: string;
  path: string;
  number: string;
  title: string;
  content: string;
}

// Cache for slide data to prevent re-fetching on every navigation
const slideDataCache = new Map<string, { data: SlideData[], timestamp: number }>()
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

export const Route = createFileRoute('/chat/$conversationId/slide/$slideHash')({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      slide: search.slide as string | undefined,
      fromTab: search.fromTab as string | undefined,
    }
  },
  component: SlideRoute,
  loader: async ({ params, location }) => {
    // Validate that conversationId exists and is valid
    if (!params.conversationId || !params.slideHash) {
      throw notFound()
    }

    // Check if we're coming from templates tab
    const searchParams = new URLSearchParams(location.search)
    const fromTab = searchParams.get('fromTab')

    // Handle template preview
    if (fromTab === 'templates') {
      console.log('Slide route - Loading template preview')

      try {
        // Fetch all public templates
        const templatesResponse = await templateAPI.listPublicTemplates()

        if (!templatesResponse.success) {
          throw notFound()
        }

        // Flatten hierarchical structure to flat list for compatibility
        interface FlatTemplate {
          id: string
          name: string
          relativePath: string
          slideJsonUrl: string
          previewUrl?: string
          metadata?: any
        }

        const flatTemplates: FlatTemplate[] = []

        for (const item of templatesResponse.templates) {
          if (item.type === 'parent') {
            // Add all child layouts from parent template
            for (const layout of item.layouts) {
              flatTemplates.push({
                id: layout.id,
                name: layout.name,
                relativePath: layout.id,
                slideJsonUrl: layout.slideJsonUrl,
                previewUrl: layout.previewUrl,
                metadata: layout.metadata,
              })
            }
          } else {
            // Add standalone layout
            flatTemplates.push({
              id: item.id,
              name: item.name,
              relativePath: item.id,
              slideJsonUrl: item.slideJsonUrl,
              previewUrl: item.previewUrl,
              metadata: item.metadata,
            })
          }
        }

        // Get template path from hash
        const templatePath = slidePathMapping.getPathFromHash(params.slideHash)

        if (!templatePath) {
          // Try to populate mappings with all template paths first
          const templatePaths = flatTemplates.map(t => t.relativePath)
          await slidePathMapping.populateMappings(templatePaths)
          const newMappedPath = slidePathMapping.getPathFromHash(params.slideHash)

          if (!newMappedPath) {
            throw notFound()
          }
        }

        const resolvedPath = slidePathMapping.getPathFromHash(params.slideHash)

        // Find matching template
        const template = flatTemplates.find(t => t.relativePath === resolvedPath)

        if (!template) {
          throw notFound()
        }

        // Convert template to slide format compatible with SlidePreview
        const slideData: SlideData = {
          id: template.id,
          path: template.relativePath,
          number: '001',
          title: template.name,
          content: '' // Will be loaded by SlidePreview
        }

        // Fetch conversation for context (still need conversationId for routing)
        const response = await conversationAPI.getById(params.conversationId)

        return {
          conversation: response.conversation,
          slideHash: params.slideHash,
          slidePath: template.relativePath,
          isStyleConversation: false,
          slides: [slideData], // Single template as slides array
          currentSlide: slideData,
          isTemplate: true,
          templateData: template
        }
      } catch (error) {
        console.error('Error loading template:', error)
        throw notFound()
      }
    }

    const cacheKey = `${params.conversationId}_slides`
    const now = Date.now()

    try {
      // Try to fetch the conversation to validate it exists
      const response = await conversationAPI.getById(params.conversationId)
      const conversation = response.conversation
      const isStyleConversation = conversation.collectionType === 'STYLE'

      let slides: SlideData[] = []

      // Check if we have cached slide data
      const cached = slideDataCache.get(cacheKey)
      if (cached && (now - cached.timestamp) < CACHE_TTL) {
        console.log('Slide route - Using cached slide data')
        slides = cached.data
      } else {
        // Only fetch if not cached
        console.log('Slide route - Fetching fresh slide data')
        
        if (isStyleConversation) {
          // For style conversations (template projects), fetch slides from validation results
          try {
            const validationResponse = await templateAPI.getValidationResults(params.conversationId)

            if (validationResponse.success && validationResponse.validation.slides) {
              // Convert template slides to slide format for path mapping
              // Only include slides that have GSAP JSON (slide.json)
              slides = validationResponse.validation.slides
                .filter((slide: any) => slide.hasGsapSlide && slide.slideJsonPath)
                .map((slide: any) => ({
                  id: slide.slideId,
                  path: slide.slideJsonPath, // Keep absolute path for hash mapping
                  number: String(slide.slideNumber).padStart(3, '0'),
                  title: slide.slideId.replace('_', ' '),
                  content: '' // Will be loaded later by SlidePreview
                }))

              console.log(`Loaded ${slides.length} template slides with GSAP JSON`)
            }
          } catch (error) {
            console.warn('Could not fetch template slides for style conversation:', error)
          }
        } else {
          // For video conversations, fetch slides normally (supports both HTML and GSAP .json slides)
          const slidesResponse = await slidesAPI.getByConversation(params.conversationId)
          slides = slidesResponse.slides || []
          // Note: GSAP slide detection and loading happens in SlidePreview component
        }
        
        // Cache the slide data
        slideDataCache.set(cacheKey, { data: slides, timestamp: now })
      }
      
      // Only populate mappings if needed
      let slidePath: string | undefined
      
      if (slides.length > 0) {
        // First check if we already have the mapping
        const mappedPath = slidePathMapping.getPathFromHash(params.slideHash)
        slidePath = mappedPath || undefined
        
        if (!slidePath) {
          // Only populate if we don't have the mapping
          console.log('Slide route - Hash not in mapping, populating paths')
          const slidePaths = slides.map(slide => slide.path)
          await slidePathMapping.populateMappings(slidePaths)
          const newMappedPath = slidePathMapping.getPathFromHash(params.slideHash)
          slidePath = newMappedPath || undefined
        } else {
          console.log('Slide route - Hash already mapped, skipping population')
        }
        
        if (!slidePath) {
          // If hash still not found, redirect to the first slide with a valid hash
          const firstSlidePath = slides[0].path
          const validHash = await slidePathMapping.generateHashForPath(firstSlidePath)
          throw redirect({
            to: '/chat/$conversationId/slide/$slideHash',
            params: {
              conversationId: params.conversationId,
              slideHash: validHash
            },
            search: {
              slide: undefined,
              fromTab: undefined
            }
          })
        }
      } else {
        throw notFound()
      }
      
      console.log('Slide route - Using path for hash:', slidePath)
      
      // Find the current slide data
      const currentSlide = slides.find(s => s.path === slidePath)
      
      return {
        conversation: response.conversation,
        slideHash: params.slideHash,
        slidePath,
        isStyleConversation,
        slides, // Pass all cached slides
        currentSlide // Pass current slide data
      }
    } catch (error) {
      // If conversation doesn't exist, throw notFound
      throw notFound()
    }
  },
})

function SlideRoute() {
  const { conversationId } = useParams({ from: '/chat/$conversationId/slide/$slideHash' })
  const searchParams = Route.useSearch()
  const navigate = useNavigate()
  const loaderData = Route.useLoaderData()

  const handleBackToGallery = () => {
    // Navigate back to gallery route with the tab that was active before viewing the slide
    navigate({
      to: '/chat/$conversationId/gallery',
      params: {
        conversationId
      },
      search: {
        slide: undefined,
        tab: searchParams.fromTab as 'scenes' | 'layouts' | 'video' | 'script' | 'audio' | 'settings' | 'resources' | 'templates' | undefined || 'scenes' // Restore the tab or default to scenes
      }
    })
  }

  return (
    <div className="h-full w-full">
      <SlidePreview
        conversationId={conversationId}
        selectedSlidePath={loaderData.slidePath}
        onBackToGallery={handleBackToGallery}
        cachedSlides={loaderData.slides}
        cachedCurrentSlide={loaderData.currentSlide}
        isTemplate={loaderData.isTemplate}
        templateSlideJsonUrl={loaderData.templateData?.slideJsonUrl}
      />
    </div>
  )
}